import React from 'react';
import styled from 'styled-components';
import {
  ExpandEvent,
  FoldEvent,
  InfoPopupBreakdown,
  InfoPopupFooter,
  InfoPopupGlobalFilter,
  InfoPopupHeader,
  InfoPopupSubHeader,
  MergeEvent,
} from '../../components';
import * as BuilderInfoUI from '../../BuilderInfoUI';
import PathStartEndConditions from './PathStartEndConditions';
import { assertCondition } from '../../../../utils/types';
import { QueryType } from '../../../../utils/apollo/graphqlSchema';
import useAnalysisBuilderInfo from '../../../../redux/selectorHooks/useAnalysisBuilderInfo';
import { theme } from '../../../../styles/theme';

interface IProps {
  analysisId: string;
  onShowInReport?: () => void;
}

const EventsContainer = styled(BuilderInfoUI.VerticalRow)`
  &:not(:first-child) {
    padding-top: 8px;
    border-top: 1px solid ${theme.colors.border};
  }
`;

export default ({ analysisId, onShowInReport }: IProps) => {
  const analysisBuilderInfo = useAnalysisBuilderInfo(analysisId);
  assertCondition(
    analysisBuilderInfo !== null && analysisBuilderInfo?.__typename === 'Path',
  );
  const {
    query: builderInfo,
    display,
    executedAt,
    description,
    creator,
  } = analysisBuilderInfo;

  const breakdown = builderInfo.breakdown;
  const expandEvents = builderInfo.expandEvents;
  const mergeEvents = builderInfo.mergeEvents;
  const foldEvents = builderInfo.foldEvents;
  const globalCohorts = builderInfo.cohortRefs;

  return (
    <>
      <InfoPopupHeader
        name={display}
        description={description}
        creator={creator}
        createdAt={executedAt}
      />
      <BuilderInfoUI.MainContent>
        <InfoPopupSubHeader analysisId={analysisId} />
        <PathStartEndConditions
          directionKey={builderInfo.direction.key}
          startConditions={builderInfo.startConditions}
          endConditions={builderInfo.endConditions}
        />
        {(builderInfo?.filter || globalCohorts.length > 0) && (
          <InfoPopupGlobalFilter
            filter={builderInfo?.filter}
            globalCohorts={globalCohorts}
          />
        )}
        {breakdown && (
          <InfoPopupBreakdown
            breakdown={breakdown}
            queryType={QueryType.Path}
          />
        )}
        {((expandEvents && expandEvents?.length > 0) ||
          (mergeEvents && mergeEvents?.length > 0) ||
          (foldEvents && foldEvents?.length > 0)) && (
          <BuilderInfoUI.VerticalRow>
            {expandEvents && expandEvents?.length > 0 && (
              <EventsContainer>
                {expandEvents.map((expandEvent, index) => (
                  <ExpandEvent
                    key={`expand-event-${index}`}
                    eventCondition={expandEvent.eventCondition}
                    field={expandEvent.field}
                    index={index}
                  />
                ))}
              </EventsContainer>
            )}
            {mergeEvents && mergeEvents?.length > 0 && (
              <EventsContainer>
                {mergeEvents.map((mergeEvent, index) => (
                  <MergeEvent
                    key={`merge-event-${index}`}
                    eventConditions={mergeEvent.eventConditions}
                    isNameAutoGenerated={mergeEvent.isNameAutoGenerated}
                    name={mergeEvent.name}
                    index={index}
                  />
                ))}
              </EventsContainer>
            )}
            {foldEvents && foldEvents?.length > 0 && (
              <EventsContainer>
                {foldEvents.map((foldEvent, index) => (
                  <FoldEvent
                    key={`fold-event-${index}`}
                    eventCondition={foldEvent.eventCondition}
                    index={index}
                  />
                ))}
              </EventsContainer>
            )}
          </BuilderInfoUI.VerticalRow>
        )}
      </BuilderInfoUI.MainContent>
      <InfoPopupFooter
        analysisId={analysisId}
        onShowInReport={onShowInReport}
      />
    </>
  );
};
